/**
 * Feature flags tab — per-org flag overrides + per-user grants.
 */
import { useState, useEffect } from 'react';
import { api } from '../../../api';
import ReasonField from '../../../components/ops_admin/ReasonField';

const CUSTOM_KEY = '__custom__';

/**
 * Flag-key picker: dropdown of known grantable flags (from
 * GET /ops_admin/flags/catalog) with a "Custom…" escape hatch for keys
 * that aren't in the catalog yet. Exact-typing a key is error-prone —
 * a silent typo just creates an override nothing reads.
 */
function FlagKeySelect({ catalog, value, onChange }) {
  const known = catalog.some((f) => f.flag_key === value);
  const isCustom = value !== '' && !known;
  const [customMode, setCustomMode] = useState(false);
  const showInput = customMode || isCustom;

  return (
    <div>
      <select
        className="form-input"
        value={showInput ? CUSTOM_KEY : value}
        onChange={(e) => {
          if (e.target.value === CUSTOM_KEY) {
            setCustomMode(true);
            onChange('');
          } else {
            setCustomMode(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">Select flag…</option>
        {catalog.map((f) => (
          <option key={f.flag_key} value={f.flag_key} title={f.description}>
            {f.flag_key}
          </option>
        ))}
        <option value={CUSTOM_KEY}>Custom key…</option>
      </select>
      {showInput && (
        <input
          className="form-input"
          style={{ marginTop: 4 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Exact flag key, e.g. ENABLE_SENTENCING_TOOLS"
        />
      )}
    </div>
  );
}

export default function OrgFlagsTab({ org, opsRole }) {
  const [flags, setFlags] = useState([]);
  const [userGrants, setUserGrants] = useState([]);
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newFlag, setNewFlag] = useState({ flag_key: '', enabled: true, reason: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const canMutate = ['admin', 'owner'].includes(opsRole);

  async function fetchFlags() {
    setLoading(true);
    try {
      const [flagsData, grantsData, membersData, catalogData] = await Promise.all([
        api.get(`/ops_admin/orgs/${org.id}/flags`),
        api.get(`/ops_admin/orgs/${org.id}/user_flags`),
        api.get(`/ops_admin/orgs/${org.id}/members`),
        api.get('/ops_admin/flags/catalog'),
      ]);
      setFlags(flagsData.flags || []);
      setUserGrants(grantsData.grants || []);
      setMembers(membersData.members || []);
      setCatalog(catalogData.flags || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { fetchFlags(); }, [org.id]);

  async function handleToggle(flag_key, enabled, reason) {
    setSaving(true); setMsg(null);
    try {
      await api.patch(`/ops_admin/orgs/${org.id}/flags`, { flag_key, enabled, reason });
      await fetchFlags();
      setMsg('Updated');
    } catch (e) { setMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!newFlag.flag_key.trim() || !newFlag.reason.trim()) return;
    await handleToggle(newFlag.flag_key, newFlag.enabled, newFlag.reason);
    setNewFlag({ flag_key: '', enabled: true, reason: '' });
  }

  if (loading) return <p className="loading-text">Loading flags...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="ops-tab">
      <h2>Feature Flag Overrides</h2>

      <table className="ops-table">
        <thead>
          <tr><th>Flag Key</th><th>Enabled</th><th>Set By</th><th>Set At</th><th>Expires</th>{canMutate && <th></th>}</tr>
        </thead>
        <tbody>
          {flags.length === 0 && <tr><td colSpan={6} className="empty-row">No overrides</td></tr>}
          {flags.map(f => (
            <FlagRow key={f.flag_key} flag={f} canMutate={canMutate} onToggle={handleToggle} saving={saving} />
          ))}
        </tbody>
      </table>

      {canMutate && (
        <div className="ops-section">
          <h3>Add / Update Flag</h3>
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Flag Key</label>
                <FlagKeySelect
                  catalog={catalog}
                  value={newFlag.flag_key}
                  onChange={(flag_key) => setNewFlag(f => ({...f, flag_key}))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Enabled</label>
                <select className="form-input" value={newFlag.enabled ? 'true' : 'false'}
                  onChange={e => setNewFlag(f => ({...f, enabled: e.target.value === 'true'}))}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <ReasonField value={newFlag.reason} onChange={r => setNewFlag(f => ({...f, reason: r}))} />
            <button type="submit" className="btn btn-primary" disabled={saving}>Set Flag</button>
          </form>
        </div>
      )}

      <UserGrantsSection
        org={org}
        grants={userGrants}
        members={members}
        catalog={catalog}
        canMutate={canMutate}
        onChanged={fetchFlags}
        setMsg={setMsg}
      />

      {msg && <p className="save-msg">{msg}</p>}
    </div>
  );
}

function UserGrantsSection({ org, grants, members, catalog, canMutate, onChanged, setMsg }) {
  const [newGrant, setNewGrant] = useState({ member_id: '', flag_key: '', enabled: true, reason: '' });
  const [saving, setSaving] = useState(false);

  async function setGrant(memberId, flag_key, enabled, reason) {
    setSaving(true); setMsg(null);
    try {
      await api.patch(`/ops_admin/orgs/${org.id}/members/${memberId}/flags`, { flag_key, enabled, reason });
      await onChanged();
      setMsg('User grant updated');
    } catch (e) { setMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  async function removeGrant(memberId, flag_key) {
    const reason = window.prompt(`Reason for removing ${flag_key} grant?`);
    if (!reason || !reason.trim()) return;
    setSaving(true); setMsg(null);
    try {
      await api.del(`/ops_admin/orgs/${org.id}/members/${memberId}/flags/${encodeURIComponent(flag_key)}?reason=${encodeURIComponent(reason)}`);
      await onChanged();
      setMsg('User grant removed');
    } catch (e) { setMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!newGrant.member_id || !newGrant.flag_key.trim() || !newGrant.reason.trim()) return;
    await setGrant(newGrant.member_id, newGrant.flag_key.trim(), newGrant.enabled, newGrant.reason);
    setNewGrant({ member_id: '', flag_key: '', enabled: true, reason: '' });
  }

  return (
    <div className="ops-section">
      <h2>Per-User Flag Grants</h2>
      <p className="help-text">
        A user grant overrides the org flag for one member (user &gt; org &gt; global default).
        Remove the grant to revert the member to org-level behavior.
      </p>

      <table className="ops-table">
        <thead>
          <tr><th>Member</th><th>Flag Key</th><th>Enabled</th><th>Set By</th><th>Set At</th><th>Expires</th>{canMutate && <th></th>}</tr>
        </thead>
        <tbody>
          {grants.length === 0 && <tr><td colSpan={7} className="empty-row">No user grants</td></tr>}
          {grants.map(g => (
            <tr key={`${g.user_id}:${g.flag_key}`}>
              <td>{g.email || g.user_id}</td>
              <td className="mono">{g.flag_key}</td>
              <td><span className={g.enabled ? 'badge badge-active' : 'badge badge-suspended'}>{g.enabled ? 'Yes' : 'No'}</span></td>
              <td>{g.set_by || '—'}</td>
              <td>{g.set_at ? new Date(g.set_at).toLocaleDateString() : '—'}</td>
              <td>{g.expires_at ? new Date(g.expires_at).toLocaleDateString() : 'Never'}</td>
              {canMutate && (
                <td>
                  {g.member_id ? (
                    <button className="btn btn-sm btn-ghost" onClick={() => removeGrant(g.member_id, g.flag_key)} disabled={saving}>
                      Remove
                    </button>
                  ) : <span className="help-text">no member row</span>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {canMutate && (
        <form onSubmit={handleCreate}>
          <h3>Add / Update User Grant</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Member</label>
              <select className="form-input" value={newGrant.member_id}
                onChange={e => setNewGrant(g => ({...g, member_id: e.target.value}))}>
                <option value="">Select member…</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.email || m.display_name || m.user_id}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Flag Key</label>
              <FlagKeySelect
                catalog={catalog}
                value={newGrant.flag_key}
                onChange={(flag_key) => setNewGrant(g => ({...g, flag_key}))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Enabled</label>
              <select className="form-input" value={newGrant.enabled ? 'true' : 'false'}
                onChange={e => setNewGrant(g => ({...g, enabled: e.target.value === 'true'}))}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
          <ReasonField value={newGrant.reason} onChange={r => setNewGrant(g => ({...g, reason: r}))} />
          <button type="submit" className="btn btn-primary" disabled={saving}>Set User Grant</button>
        </form>
      )}
    </div>
  );
}

function FlagRow({ flag, canMutate, onToggle, saving }) {
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);

  return (
    <tr>
      <td className="mono">{flag.flag_key}</td>
      <td><span className={flag.enabled ? 'badge badge-active' : 'badge badge-suspended'}>{flag.enabled ? 'Yes' : 'No'}</span></td>
      <td>{flag.set_by || '—'}</td>
      <td>{flag.set_at ? new Date(flag.set_at).toLocaleDateString() : '—'}</td>
      <td>{flag.expires_at ? new Date(flag.expires_at).toLocaleDateString() : 'Never'}</td>
      {canMutate && (
        <td>
          {editing ? (
            <div>
              <ReasonField value={reason} onChange={setReason} placeholder="Reason..." />
              <button className="btn btn-sm btn-primary"
                onClick={() => { onToggle(flag.flag_key, !flag.enabled, reason); setEditing(false); setReason(''); }}
                disabled={saving || !reason.trim()}>
                {flag.enabled ? 'Disable' : 'Enable'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          ) : (
            <button className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>Toggle</button>
          )}
        </td>
      )}
    </tr>
  );
}
